import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '../components/layout/AppShell'
import { orgService } from '../services/orgService'
import type { Race } from '../types/index'

type Tab = 'users' | 'races'

type UserRole = 'system_admin' | 'race_organizer' | 'team_admin' | 'runner' | 'spectator' | 'viewer'

interface ManagedUser {
  id: number
  username: string
  role: UserRole
  firstName: string | null
  lastName: string | null
  phone: string | null
  email: string | null
  createdAt: string
  lastLoginAt: string | null
  deviceCount: number
  teamId: number | null
  teamName: string | null
}

const ROLE_LABELS: Record<UserRole, { label: string; cls: string }> = {
  system_admin:   { label: 'System Admin',    cls: 'bg-red-100 text-red-800' },
  race_organizer: { label: 'Race Organizer',  cls: 'bg-indigo-100 text-indigo-800' },
  team_admin:     { label: 'Team Admin',      cls: 'bg-purple-100 text-purple-800' },
  runner:         { label: 'Runner',          cls: 'bg-green-100 text-green-800' },
  spectator:      { label: 'Spectator',       cls: 'bg-gray-100 text-gray-600' },
  viewer:         { label: 'Viewer',          cls: 'bg-gray-100 text-gray-600' },
}

// ── Reusable race config fields (used in both Create and Edit modals) ─────────

const ALL_TEAM_SIZES = [2, 3, 4, 6, 12]

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Denver',      label: 'Mountain (MT)' },
  { value: 'America/Chicago',     label: 'Central (CT)' },
  { value: 'America/New_York',    label: 'Eastern (ET)' },
  { value: 'America/Phoenix',     label: 'Arizona (no DST)' },
  { value: 'America/Anchorage',   label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (HST)' },
]

interface RaceConfigFieldsProps {
  races: Race[]
  excludeRaceId: number | undefined
  parentRaceId: number | ''
  setParentRaceId: (v: number | '') => void
  showLegRange?: boolean
  startLeg: string
  setStartLeg: (v: string) => void
  endLeg: string
  setEndLeg: (v: string) => void
  allowedSizes: number[]
  toggleSize: (size: number) => void
  maxTeamSize: number
  setMaxTeamSize: (v: number) => void
  timezone: string
  setTimezone: (v: string) => void
}

function RaceConfigFields({
  races, excludeRaceId, parentRaceId, setParentRaceId,
  showLegRange = false, startLeg, setStartLeg, endLeg, setEndLeg,
  allowedSizes, toggleSize, maxTeamSize, setMaxTeamSize,
  timezone, setTimezone,
}: RaceConfigFieldsProps) {
  const parentRaces = races.filter(r => r.id !== excludeRaceId)

  return (
    <div className="border-t border-gray-100 pt-3 space-y-3">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Race Configuration</p>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Clone from</label>
        <select
          value={parentRaceId}
          onChange={e => {
            const v = e.target.value
            setParentRaceId(v === '' ? '' : Number(v))
            if (v === '') { setStartLeg(''); setEndLeg('') }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">— None (start from scratch) —</option>
          {parentRaces.map(r => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        {parentRaceId !== '' && (
          <p className="text-xs text-gray-400 mt-1">Course legs, waypoints, and van routes will be copied. Leave leg range blank to copy all legs.</p>
        )}
      </div>

      {showLegRange && parentRaceId !== '' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Start Leg *</label>
            <input
              type="number" value={startLeg} min={1} max={36}
              onChange={e => setStartLeg(e.target.value)}
              placeholder="e.g. 25"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">End Leg *</label>
            <input
              type="number" value={endLeg} min={1} max={36}
              onChange={e => setEndLeg(e.target.value)}
              placeholder="e.g. 26"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Allowed Team Sizes</label>
        <div className="flex gap-2 flex-wrap">
          {ALL_TEAM_SIZES.map(size => (
            <button
              key={size}
              type="button"
              onClick={() => toggleSize(size)}
              className={`px-3 py-1.5 text-sm rounded-lg border font-medium transition-colors ${
                allowedSizes.includes(size)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-1">Select all valid team sizes for this race.</p>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Max Team Size</label>
        <select
          value={maxTeamSize}
          onChange={e => setMaxTeamSize(Number(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {(allowedSizes.length > 0 ? allowedSizes : ALL_TEAM_SIZES).map(size => (
            <option key={size} value={size}>{size} runners</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Race Timezone</label>
        <select
          value={timezone}
          onChange={e => setTimezone(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {TIMEZONES.map(tz => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-1">All start times and timestamps for this race will display in this timezone.</p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

export function SystemAdminPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('users')

  // Users
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null)
  const [editRole, setEditRole] = useState<UserRole>('runner')
  const [saving, setSaving] = useState(false)

  // Organizer assignment modal
  const [managingRace, setManagingRace] = useState<Race | null>(null)
  const [raceOrganizers, setRaceOrganizers] = useState<Array<{ id: number; username: string; firstName: string | null; lastName: string | null; email: string | null; assignedAt: string }>>([])
  const [orgLoading, setOrgLoading] = useState(false)
  const [addingOrgId, setAddingOrgId] = useState<number | ''>('')

  // Races
  const [races, setRaces] = useState<Race[]>([])
  const [racesLoading, setRacesLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editingRace, setEditingRace] = useState<Race | null>(null)
  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [newDate, setNewDate] = useState('')
  const [newParentRaceId, setNewParentRaceId] = useState<number | ''>('')
  const [newStartLeg, setNewStartLeg] = useState('')
  const [newEndLeg, setNewEndLeg] = useState('')
  const [newAllowedSizes, setNewAllowedSizes] = useState<number[]>([12, 6])
  const [newMaxTeamSize, setNewMaxTeamSize] = useState(12)
  const [newTimezone, setNewTimezone] = useState('America/Los_Angeles')

  const [error, setError] = useState<string | null>(null)

  const toSlug = (name: string) => name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '')

  useEffect(() => {
    if (tab === 'users') {
      setUsersLoading(true)
      orgService.getUsers()
        .then(setUsers)
        .catch(() => setError('Failed to load users'))
        .finally(() => setUsersLoading(false))
    } else {
      setRacesLoading(true)
      // Load races and users together so the Organizers modal can populate the dropdown
      Promise.all([orgService.getRaces(), users.length === 0 ? orgService.getUsers() : Promise.resolve(users)])
        .then(([r, u]) => { setRaces(r); setUsers(u) })
        .catch(() => setError('Failed to load races'))
        .finally(() => setRacesLoading(false))
    }
  }, [tab])

  const openUser = (u: ManagedUser) => {
    setSelectedUser(u)
    setEditRole(u.role)
  }

  const handleSaveRole = async () => {
    if (!selectedUser) return
    setSaving(true)
    try {
      await orgService.updateUserRole(selectedUser.id, editRole)
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, role: editRole } : u))
      setSelectedUser(null)
    } catch {
      setError('Failed to update role')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteUser = async (u: ManagedUser) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return
    try {
      await orgService.deleteUser(u.id)
      setUsers(prev => prev.filter(x => x.id !== u.id))
      setSelectedUser(null)
    } catch {
      setError('Failed to delete user')
    }
  }

  const resetRaceForm = () => {
    setNewName(''); setNewSlug(''); setNewDate('')
    setNewParentRaceId(''); setNewStartLeg(''); setNewEndLeg('')
    setNewAllowedSizes([12, 6]); setNewMaxTeamSize(12)
    setNewTimezone('America/Los_Angeles')
  }

  const toggleAllowedSize = (size: number) => {
    setNewAllowedSizes(prev => {
      const next = prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size].sort((a, b) => b - a)
      setNewMaxTeamSize(next.length > 0 ? Math.max(...next) : 12)
      return next
    })
  }

  const handleCreateRace = async () => {
    if (!newName.trim()) return
    try {
      const slug = newSlug.trim() || toSlug(newName.trim())
      const race = await orgService.createRace({
        name: newName.trim(), slug, eventDate: newDate || undefined,
        timezone: newTimezone,
        parentRaceId: newParentRaceId !== '' ? Number(newParentRaceId) : undefined,
        courseStartLeg: newStartLeg ? parseInt(newStartLeg) : undefined,
        courseEndLeg: newEndLeg ? parseInt(newEndLeg) : undefined,
        allowedTeamSizes: newAllowedSizes.length > 0 ? newAllowedSizes : [12, 6],
        maxTeamSize: newMaxTeamSize,
      })
      setRaces(prev => [race, ...prev])
      resetRaceForm()
      setCreating(false)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to create race')
    }
  }

  const openEditRace = (race: Race, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingRace(race)
    setNewName(race.name)
    setNewSlug(race.slug ?? '')
    setNewDate(race.eventDate ? new Date(race.eventDate).toISOString().split('T')[0] : '')
    setNewParentRaceId(race.parentRaceId ?? '')
    setNewStartLeg(race.courseStartLeg != null ? String(race.courseStartLeg) : '')
    setNewEndLeg(race.courseEndLeg != null ? String(race.courseEndLeg) : '')
    const sizes = race.allowedTeamSizes ?? [12, 6]
    setNewAllowedSizes(sizes)
    setNewMaxTeamSize(race.maxTeamSize ?? Math.max(...sizes))
    setNewTimezone(race.timezone ?? 'America/Los_Angeles')
  }

  const handleUpdateRace = async () => {
    if (!editingRace || !newName.trim()) return
    setSaving(true)
    try {
      const updated = await orgService.updateRace(editingRace.id, {
        name: newName.trim(),
        slug: newSlug.trim() || toSlug(newName.trim()),
        eventDate: newDate || undefined,
        timezone: newTimezone,
        parentRaceId: newParentRaceId !== '' ? Number(newParentRaceId) : null,
        courseStartLeg: newStartLeg ? parseInt(newStartLeg) : null,
        courseEndLeg: newEndLeg ? parseInt(newEndLeg) : null,
        allowedTeamSizes: newAllowedSizes.length > 0 ? newAllowedSizes : [12, 6],
        maxTeamSize: newMaxTeamSize,
      })
      setRaces(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r))
      setEditingRace(null)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to update race')
    } finally {
      setSaving(false)
    }
  }

  const openManageOrganizers = async (race: Race, e: React.MouseEvent) => {
    e.stopPropagation()
    setManagingRace(race)
    setAddingOrgId('')
    setOrgLoading(true)
    try {
      const orgs = await orgService.getRaceOrganizers(race.id)
      setRaceOrganizers(orgs)
    } catch {
      setError('Failed to load organizers')
    } finally {
      setOrgLoading(false)
    }
  }

  const handleAddOrganizer = async () => {
    if (!managingRace || addingOrgId === '') return
    setSaving(true)
    try {
      await orgService.addRaceOrganizer(managingRace.id, Number(addingOrgId))
      const orgs = await orgService.getRaceOrganizers(managingRace.id)
      setRaceOrganizers(orgs)
      setAddingOrgId('')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to add organizer')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveOrganizer = async (userId: number) => {
    if (!managingRace) return
    try {
      await orgService.removeRaceOrganizer(managingRace.id, userId)
      setRaceOrganizers(prev => prev.filter(o => o.id !== userId))
    } catch {
      setError('Failed to remove organizer')
    }
  }

  const handleDeleteRace = async (race: Race, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!window.confirm(`Delete "${race.name}"?\n\nThis will permanently delete all teams, runners, legs, logs, and location data for this race. This cannot be undone.`)) return
    if (!window.confirm(`Are you absolutely sure? Type OK to confirm deletion of "${race.name}".`)) return
    try {
      await orgService.deleteRace(race.id)
      setRaces(prev => prev.filter(r => r.id !== race.id))
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to delete race')
    }
  }

  const statusBadge = (status: Race['status']) => {
    const map: Record<Race['status'], { label: string; cls: string }> = {
      upcoming:  { label: 'Upcoming',  cls: 'bg-yellow-100 text-yellow-800' },
      active:    { label: 'Active',    cls: 'bg-green-100 text-green-800' },
      completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-800' },
      archived:  { label: 'Archived',  cls: 'bg-gray-100 text-gray-500' },
    }
    const { label, cls } = map[status] ?? map.archived
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
  }

  const TABS: { key: Tab; label: string }[] = [
    { key: 'users', label: 'Users' },
    { key: 'races', label: 'Races' },
  ]

  return (
    <AppShell>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-3 pb-2 flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              System Admin
            </h1>
            {tab === 'races' && (
              <button
                onClick={() => setCreating(true)}
                className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
              >
                + New Race
              </button>
            )}
          </div>
          <div className="flex gap-1">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1 text-sm rounded-md font-medium transition-colors ${
                  tab === t.key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg flex-shrink-0">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 min-h-0">

          {/* ── Users tab ── */}
          {tab === 'users' && (
            usersLoading ? (
              <p className="text-sm text-gray-400 text-center py-8">Loading...</p>
            ) : (
              <div className="space-y-2">
                {users.map(u => (
                  <div
                    key={u.id}
                    onClick={() => openUser(u)}
                    className="bg-white rounded-lg border border-gray-200 px-4 py-3 cursor-pointer hover:border-indigo-300 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900">
                            {u.firstName || u.lastName ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() : u.username}
                          </p>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_LABELS[u.role]?.cls ?? 'bg-gray-100 text-gray-600'}`}>
                            {ROLE_LABELS[u.role]?.label ?? u.role}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          @{u.username}
                          {u.email && u.email !== u.username && <span className="ml-1">· {u.email}</span>}
                          {u.teamName && <span className="ml-1 text-purple-600">· {u.teamName}</span>}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {u.lastLoginAt
                            ? `Last login: ${new Date(u.lastLoginAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}`
                            : 'Never logged in'}
                          {u.deviceCount > 0 && <span className="ml-1">· {u.deviceCount} device{u.deviceCount !== 1 ? 's' : ''}</span>}
                        </p>
                      </div>
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* ── Races tab ── */}
          {tab === 'races' && (
            racesLoading ? (
              <p className="text-sm text-gray-400 text-center py-8">Loading...</p>
            ) : races.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No races yet.</p>
            ) : (
              <div className="space-y-2">
                {races.map(race => (
                  <div
                    key={race.id}
                    onClick={() => navigate(`/org/races/${race.id}`)}
                    className="bg-white rounded-lg border border-gray-200 px-4 py-3 cursor-pointer hover:border-indigo-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{race.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {race.eventDate ? new Date(race.eventDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'No date set'}
                        </p>
                        {race.organizerRegistrationCode && (
                          <p className="text-xs mt-0.5">
                            <span className="text-gray-400">Organizer code: </span>
                            <span className="font-mono font-bold tracking-widest text-amber-600">{race.organizerRegistrationCode}</span>
                          </p>
                        )}
                        {race.slug && <p className="text-xs font-mono text-indigo-500 mt-0.5">/watch/{race.slug}/BIB</p>}
                        {race.parentRaceId != null && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Cloned from: {races.find(r => r.id === race.parentRaceId)?.name ?? `Race #${race.parentRaceId}`}
                            {race.courseStartLeg != null && race.courseEndLeg != null && (
                              <span className="ml-1">· Legs {race.courseStartLeg}–{race.courseEndLeg}</span>
                            )}
                          </p>
                        )}
                        {race.allowedTeamSizes && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Team sizes: {(race.allowedTeamSizes ?? []).join(', ')} runners
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {statusBadge(race.status)}
                        <button
                          onClick={e => openManageOrganizers(race, e)}
                          className="text-xs text-purple-600 hover:underline px-1"
                        >Organizers</button>
                        <button
                          onClick={e => openEditRace(race, e)}
                          className="text-xs text-indigo-600 hover:underline px-1"
                        >Edit</button>
                        {!race.protected && (
                          <button
                            onClick={e => handleDeleteRace(race, e)}
                            className="text-xs text-red-500 hover:text-red-700 hover:underline px-1"
                          >Delete</button>
                        )}
                        {race.protected && (
                          <span className="text-xs text-gray-300 px-1" title="This race is protected and cannot be deleted">🔒</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

        </div>
      </div>

      {/* Edit user modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm space-y-3">
            <h2 className="text-lg font-bold">Edit User</h2>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {selectedUser.firstName || selectedUser.lastName
                  ? `${selectedUser.firstName ?? ''} ${selectedUser.lastName ?? ''}`.trim()
                  : selectedUser.username}
              </p>
              <p className="text-xs text-gray-500">@{selectedUser.username}</p>
              {selectedUser.email && <p className="text-xs text-gray-500">{selectedUser.email}</p>}
              {selectedUser.phone && <p className="text-xs text-gray-500">{selectedUser.phone}</p>}
              {selectedUser.teamName && <p className="text-xs text-purple-600 mt-1">Team: {selectedUser.teamName}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Role</label>
              <select
                value={editRole}
                onChange={e => setEditRole(e.target.value as UserRole)}
                disabled={selectedUser.role === 'system_admin'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50"
              >
                <option value="system_admin">System Admin</option>
                <option value="race_organizer">Race Organizer</option>
                <option value="team_admin">Team Admin</option>
                <option value="runner">Runner</option>
                <option value="spectator">Spectator</option>
                <option value="viewer">Viewer</option>
              </select>
              {selectedUser.role === 'system_admin' && (
                <p className="text-xs text-gray-400 mt-1">System admin role cannot be changed here.</p>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setSelectedUser(null)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >Cancel</button>
              {selectedUser.role !== 'system_admin' && (
                <button
                  onClick={() => handleDeleteUser(selectedUser)}
                  className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                >Delete</button>
              )}
              <button
                onClick={handleSaveRole}
                disabled={saving || selectedUser.role === 'system_admin'}
                className="flex-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage race organizers modal */}
      {managingRace && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm space-y-4 max-h-[90vh] overflow-y-auto">
            <div>
              <h2 className="text-lg font-bold">Race Organizers</h2>
              <p className="text-xs text-gray-500 mt-0.5">{managingRace.name}</p>
            </div>

            {/* Current organizers */}
            {orgLoading ? (
              <p className="text-sm text-gray-400 text-center py-4">Loading...</p>
            ) : raceOrganizers.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-2">No organizers assigned yet.</p>
            ) : (
              <div className="space-y-2">
                {raceOrganizers.map(org => (
                  <div key={org.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {org.firstName || org.lastName
                          ? `${org.firstName ?? ''} ${org.lastName ?? ''}`.trim()
                          : org.username}
                      </p>
                      <p className="text-xs text-gray-500">
                        @{org.username}
                        {org.email && org.email !== org.username && <span className="ml-1">· {org.email}</span>}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveOrganizer(org.id)}
                      className="text-xs text-red-500 hover:text-red-700 hover:underline flex-shrink-0 ml-2"
                    >Remove</button>
                  </div>
                ))}
              </div>
            )}

            {/* Add organizer */}
            <div className="border-t border-gray-100 pt-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Add Organizer</label>
              <div className="flex gap-2">
                <select
                  value={addingOrgId}
                  onChange={e => setAddingOrgId(e.target.value === '' ? '' : Number(e.target.value))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">— Select user —</option>
                  {users
                    .filter(u => (u.role === 'race_organizer' || u.role === 'system_admin') && !raceOrganizers.some(o => o.id === u.id))
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {u.firstName || u.lastName ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() : u.username}
                        {' '}(@{u.username})
                      </option>
                    ))
                  }
                </select>
                <button
                  onClick={handleAddOrganizer}
                  disabled={addingOrgId === '' || saving}
                  className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >Add</button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Only race_organizer and system_admin users appear here.</p>
            </div>

            <button
              onClick={() => setManagingRace(null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >Done</button>
          </div>
        </div>
      )}

      {/* Edit race modal */}
      {editingRace && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm space-y-3 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold">Edit Race</h2>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Race Name *</label>
              <input
                type="text" value={newName} autoFocus
                onChange={e => { setNewName(e.target.value); if (!editingRace.slug) setNewSlug(toSlug(e.target.value)) }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Spectator URL Slug</label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400 whitespace-nowrap">/watch/</span>
                <input
                  type="text" value={newSlug}
                  onChange={e => setNewSlug(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-400 whitespace-nowrap">/BIB</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Event Date</label>
              <input
                type="date" value={newDate}
                onChange={e => setNewDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <RaceConfigFields
              races={races} excludeRaceId={editingRace.id}
              parentRaceId={newParentRaceId} setParentRaceId={setNewParentRaceId}
              showLegRange
              startLeg={newStartLeg} setStartLeg={setNewStartLeg}
              endLeg={newEndLeg} setEndLeg={setNewEndLeg}
              allowedSizes={newAllowedSizes} toggleSize={toggleAllowedSize}
              maxTeamSize={newMaxTeamSize} setMaxTeamSize={setNewMaxTeamSize}
              timezone={newTimezone} setTimezone={setNewTimezone}
            />
            <div className="flex gap-2 pt-1">
              <button onClick={() => setEditingRace(null)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleUpdateRace} disabled={!newName.trim() || saving} className="flex-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Create race modal */}
      {creating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm space-y-3 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold">Create New Race</h2>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Race Name *</label>
              <input
                type="text" value={newName} autoFocus
                onChange={e => { setNewName(e.target.value); setNewSlug(toSlug(e.target.value)) }}
                placeholder="Reno Tahoe Odyssey 2027"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Spectator URL Slug</label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400 whitespace-nowrap">/watch/</span>
                <input
                  type="text" value={newSlug}
                  onChange={e => setNewSlug(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="RTO2027"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-400 whitespace-nowrap">/BIB</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Event Date</label>
              <input
                type="date" value={newDate}
                onChange={e => setNewDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <RaceConfigFields
              races={races} excludeRaceId={undefined}
              parentRaceId={newParentRaceId} setParentRaceId={setNewParentRaceId}
              showLegRange
              startLeg={newStartLeg} setStartLeg={setNewStartLeg}
              endLeg={newEndLeg} setEndLeg={setNewEndLeg}
              allowedSizes={newAllowedSizes} toggleSize={toggleAllowedSize}
              maxTeamSize={newMaxTeamSize} setMaxTeamSize={setNewMaxTeamSize}
              timezone={newTimezone} setTimezone={setNewTimezone}
            />
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setCreating(false); resetRaceForm() }} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreateRace} disabled={!newName.trim()} className="flex-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50">Create</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  )
}
